import { useCallback, useEffect, useState } from 'react';
import { ReviewSaveState } from 'pages/Intelligence/DailyNav/components/ReviewPriceCell';

// Long enough to read as a deliberate confirmation, short enough that it is over
// before the next click. Matches the animation duration on the button.
export const REVIEW_SAVE_PULSE_MS = 1500;

export interface UseReviewSaveFeedbackResult {
  // Handed to the sheet, which gives it to the one matching Review button.
  reviewSaveFeedback: ReviewSaveState | null;
  onReviewSaved: (companyId: number) => void;
}

// SCAL-10030: a save closes the review dialog and invalidates the grid, so the
// only feedback used to be a snackbar and a spinner on the freshness line --
// both far from the button that was clicked. This keeps the button itself busy
// until its own row has come back, then pulses it once.
const useReviewSaveFeedback = (isRefreshing: boolean): UseReviewSaveFeedbackResult => {
  const [savedCompanyId, setSavedCompanyId] = useState<number | null>(null);
  const [hasSettled, setHasSettled] = useState(false);

  const onReviewSaved = useCallback((companyId: number) => {
    setSavedCompanyId(companyId);
    setHasSettled(false);
  }, []);

  // The refetch has landed. A save that triggers no refetch at all (notes only,
  // on a locked day) settles on the first pass, so the button still confirms
  // rather than spinning forever.
  useEffect(() => {
    if (savedCompanyId !== null && !isRefreshing) {
      setHasSettled(true);
    }
  }, [savedCompanyId, isRefreshing]);

  // Kept apart from the effect above so that flipping `hasSettled` does not tear
  // down the timer it would have started.
  useEffect(() => {
    if (savedCompanyId === null || !hasSettled) {
      return undefined;
    }
    const timeout = setTimeout(() => setSavedCompanyId(null), REVIEW_SAVE_PULSE_MS);
    return () => clearTimeout(timeout);
  }, [savedCompanyId, hasSettled]);

  return {
    reviewSaveFeedback:
      savedCompanyId === null ? null : { companyId: savedCompanyId, feedback: hasSettled ? 'saved' : 'saving' },
    onReviewSaved,
  };
};

export default useReviewSaveFeedback;
